import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Spin,
  Table,
  Tabs,
  Tag,
  Upload,
} from 'antd'
import { ArrowLeftOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons'
import { toast } from 'react-toastify'
import type { MatriculaDaTurma } from '@/types'
import { useTurmaDetalheStore } from '@/stores/turmaDetalheStore'
import { formatCurrency, formatDate, formatCpf } from '@/lib/formatters'
import {
  calculateProgress,
  faseDoCurso,
  formatUltimaAtividade,
  ultimaAtividade,
} from '@/lib/progressoDoAluno'
import { getNonceNotaFiscal } from '@/api/turmas'
import { API_BASE_URL } from '@/api/axios'
import { situacaoDaTurma } from '@/lib/situacaoDaTurma'

export function TurmaDetalhe() {
  return (
    <div className="space-y-5">
      <CarregadorDaTurma />
      <Cabecalho />
      <Conteudo />
    </div>
  )
}

function CarregadorDaTurma() {
  const { turmaId } = useParams()
  const carregar = useTurmaDetalheStore((state) => state.carregar)
  useEffect(() => {
    if (turmaId) void carregar(Number(turmaId))
  }, [carregar, turmaId])
  return null
}

function Cabecalho() {
  const navigate = useNavigate()
  const turma = useTurmaDetalheStore((state) => state.turma)
  return (
    <div className="flex items-center gap-3">
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/turmas')} />
      <div>
        <h1 className="text-3xl font-bold leading-tight text-[#1a3a5c]">
          {turma?.descricaoTurma ?? 'Turma'}
        </h1>
        {turma && (
          <TagDeSituacao
            statusTurma={turma.statusTurma}
            dataPagamento={turma.pagamento?.dataPagamento}
            dataVencimento={turma.pagamento?.dataVencimento}
          />
        )}
      </div>
    </div>
  )
}

function Conteudo() {
  const turma = useTurmaDetalheStore((state) => state.turma)
  const loading = useTurmaDetalheStore((state) => state.loading)
  const erro = useTurmaDetalheStore((state) => state.error)
  if (loading) return <Spin size="large" />
  if (erro) return <Alert type="error" showIcon message={erro} />
  if (!turma) return null
  return (
    <Tabs
      defaultActiveKey="dados"
      items={[
        { key: 'dados', label: 'Dados da turma', children: <DadosDaTurma /> },
        { key: 'alunos', label: 'Alunos', children: <AlunosDaTurma /> },
      ]}
    />
  )
}

function DadosDaTurma() {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <BlocoDeCobranca />
      <BlocoDeNotaFiscal />
      <BlocoDaSolicitacao />
    </div>
  )
}

function BlocoDeCobranca() {
  const turma = useTurmaDetalheStore((state) => state.turma)
  if (!turma) return null
  if (!turma.pagamento) {
    return (
      <Card title="Cobrança">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Cobrança ainda não gerada" />
      </Card>
    )
  }
  return (
    <Card title="Cobrança">
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Valor total">
          {formatCurrency(turma.pagamento.valor)}
        </Descriptions.Item>
        <Descriptions.Item label="Valor New Driver">
          {valorOuTraco(turma.pagamento.valorNewDriver)}
        </Descriptions.Item>
        <Descriptions.Item label="Seu repasse">
          {valorOuTraco(turma.repasseDoParceiro)}
        </Descriptions.Item>
        <Descriptions.Item label="Vencimento">
          {formatDate(turma.pagamento.dataVencimento)}
        </Descriptions.Item>
        <Descriptions.Item label="Situação">
          <TagDeSituacao
            statusTurma={turma.statusTurma}
            dataPagamento={turma.pagamento.dataPagamento}
            dataVencimento={turma.pagamento.dataVencimento}
          />
        </Descriptions.Item>
      </Descriptions>
    </Card>
  )
}

function BlocoDeNotaFiscal() {
  const turma = useTurmaDetalheStore((state) => state.turma)
  if (!turma) return null

  async function baixarNotaFiscal() {
    try {
      const { notaFiscalId, nonce } = await getNonceNotaFiscal(turma!.id)
      window.open(
        `${API_BASE_URL}/notas-fiscais/download/${notaFiscalId}/${nonce}`,
        '_blank',
        'noopener',
      )
    } catch {
      toast.error('Não foi possível abrir a nota fiscal.')
    }
  }

  return (
    <Card title="Nota fiscal">
      {turma.notaFiscalId ? (
        <Button icon={<DownloadOutlined />} onClick={baixarNotaFiscal}>
          Baixar nota fiscal
        </Button>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nota fiscal ainda não emitida" />
      )}
    </Card>
  )
}

function BlocoDaSolicitacao() {
  const navigate = useNavigate()
  const turma = useTurmaDetalheStore((state) => state.turma)
  const cancelar = useTurmaDetalheStore((state) => state.cancelar)
  const trocarArquivo = useTurmaDetalheStore((state) => state.trocarArquivo)
  if (!turma) return null
  if (turma.statusTurma !== 'SOLICITADA' && !turma.arquivoAlunosNome && !turma.obs) return null

  function confirmarCancelamento() {
    Modal.confirm({
      title: 'Cancelar solicitação?',
      content: 'A turma deixará de aparecer no painel.',
      okText: 'Cancelar solicitação',
      okButtonProps: { danger: true },
      cancelText: 'Voltar',
      onOk: async () => {
        await cancelar()
        toast.success('Solicitação cancelada.')
        navigate('/turmas')
      },
    })
  }

  return (
    <Card title="Solicitação" className="lg:col-span-2">
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Arquivo">{turma.arquivoAlunosNome ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Observações">{turma.obs || '—'}</Descriptions.Item>
      </Descriptions>
      {turma.statusTurma === 'SOLICITADA' && (
        <div className="flex flex-wrap gap-2">
          <Upload
            showUploadList={false}
            beforeUpload={(arquivo) => {
              void trocarArquivo(arquivo)
                .then(() => toast.success('Arquivo atualizado.'))
                .catch(() => toast.error('Não foi possível atualizar o arquivo.'))
              return false
            }}
          >
            <Button icon={<UploadOutlined />}>
              {turma.arquivoAlunosNome ? 'Trocar arquivo' : 'Adicionar arquivo'}
            </Button>
          </Upload>
          <Button danger onClick={confirmarCancelamento}>
            Cancelar solicitação
          </Button>
        </div>
      )}
    </Card>
  )
}

function AlunosDaTurma() {
  const turma = useTurmaDetalheStore((state) => state.turma)
  const alunos = turma?.matriculas ?? []
  if (!alunos.length) {
    return (
      <Empty
        description={
          turma?.statusTurma === 'SOLICITADA'
            ? 'Os alunos aparecerão aqui quando o suporte concluir a criação da turma.'
            : 'Nenhum aluno nesta turma.'
        }
      />
    )
  }
  return (
    <Table
      rowKey="id"
      dataSource={alunos}
      scroll={{ x: 'max-content' }}
      columns={[
        {
          title: 'Aluno',
          render: (_, aluno: MatriculaDaTurma) => (
            <div>
              <div className="font-semibold text-[#1a3a5c]">{aluno.aluno.nome}</div>
              <span className="text-sm text-slate-500">{formatCpf(aluno.aluno.cpf)}</span>
            </div>
          ),
        },
        {
          title: 'Curso',
          render: (_, aluno: MatriculaDaTurma) => aluno.curso.nomeReduzido || aluno.curso.nome,
        },
        {
          title: 'Progresso no curso',
          render: (_, aluno: MatriculaDaTurma) => {
            const progresso = calculateProgress(aluno.estatisticasEstudos, aluno.curso.modulos.length)
            return `${faseDoCurso(aluno.estatisticasEstudos, progresso)} · ${progresso}%`
          },
        },
        {
          title: 'Última atividade',
          render: (_, aluno: MatriculaDaTurma) =>
            formatUltimaAtividade(ultimaAtividade(aluno.estatisticasEstudos)),
        },
        {
          title: 'Data de matrícula',
          render: (_, aluno: MatriculaDaTurma) => formatDate(aluno.createdAt),
        },
      ]}
    />
  )
}

function TagDeSituacao({
  statusTurma,
  dataPagamento,
  dataVencimento,
}: {
  statusTurma: 'SOLICITADA' | 'ATIVA'
  dataPagamento?: string | null
  dataVencimento?: string | null
}) {
  const situacao = situacaoDaTurma({ statusTurma, dataPagamento, dataVencimento })
  return <Tag color={situacao.cor}>{situacao.texto}</Tag>
}

function valorOuTraco(valor: number | null | undefined): string {
  return valor == null ? '—' : formatCurrency(valor)
}
